import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bookmark, BookmarkPlus, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Every matching weekday in next calendar month, kept inside today..today+maxDaysAhead.
export const datesForNextMonth = (weekdays, maxDaysAhead = 90) => {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const limit = new Date(now.getTime() + maxDaysAhead * 86400000);
  const out = [];
  for (let d = new Date(now.getFullYear(), now.getMonth() + 1, 1); d.getMonth() === (now.getMonth() + 1) % 12; d.setDate(d.getDate() + 1)) {
    if (weekdays.includes(d.getDay()) && d >= now && d <= limit) out.push(iso(d));
  }
  return out;
};

// Saved recurring patterns: tap to fill the form, or save the current pick as a pattern.
export const LeavePatterns = ({ token, leaveType, reason, dates, onUse }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [days, setDays] = useState([]);
  const [err, setErr] = useState('');
  const [savedFor, setSavedFor] = useState('');
  const load = () => axios.get(`${API}/api/employee-portal/leave-templates`, { headers }).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const openSave = () => { setDays([...new Set(dates.map((d) => new Date(`${d}T00:00:00`).getDay()))].sort()); setName(''); setErr(''); setSaving(true); };
  const save = async () => {
    try { await axios.post(`${API}/api/employee-portal/leave-templates`, { name, leave_type: leaveType, weekdays: days, reason }, { headers }); setSaving(false); setSavedFor(dates.join()); load(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not save'); }
  };
  const remove = async (t) => { await axios.delete(`${API}/api/employee-portal/leave-templates/${t.id}`, { headers }).catch(() => {}); load(); };
  const nextMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('en-GB', { month: 'long' });
  const canSave = !saving && dates.length > 0 && leaveType && savedFor !== dates.join();
  if (!list.length && !canSave && !saving && savedFor !== dates.join()) return null;

  return (
    <div className="space-y-2" data-testid="leave-patterns">
      {list.length > 0 && (
        <div>
          <p className="text-[11px] text-gray-500 mb-1">My patterns — tap to fill {nextMonth}</p>
          <div className="flex flex-wrap gap-1.5">
            {list.map((t) => (
              <span key={t.id} className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 text-violet-800 text-xs">
                <button type="button" onClick={() => onUse(t)} className="pl-2.5 pr-1 py-1 flex items-center gap-1 hover:text-violet-950" data-testid={`pattern-use-${t.id}`}>
                  <Bookmark className="h-3 w-3" />{t.name} <span className="text-violet-500">({t.weekdays.map((w) => WD[w]).join(', ')})</span>
                </button>
                <button type="button" onClick={() => remove(t)} className="pr-2 pl-0.5 py-1 hover:text-red-600" title="Delete pattern" data-testid={`pattern-delete-${t.id}`}><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}
      {canSave && (
        <button type="button" onClick={openSave} className="text-xs text-violet-700 hover:underline inline-flex items-center gap-1" data-testid="pattern-save-open"><BookmarkPlus className="h-3.5 w-3.5" />Save as a reusable pattern</button>
      )}
      {!saving && dates.length > 0 && savedFor === dates.join() && <p className="text-[11px] text-emerald-700" data-testid="pattern-saved-note">Pattern saved — find it at the top next time.</p>}
      {saving && (
        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-2.5 space-y-2" data-testid="pattern-save-form">
          <input className="w-full border rounded-md px-2 py-1.5 text-sm" placeholder="Pattern name, e.g. Friday off" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} data-testid="pattern-name" />
          <div className="flex flex-wrap gap-1">
            {WD.map((w, i) => (
              <button key={w} type="button" onClick={() => setDays((d) => (d.includes(i) ? d.filter((x) => x !== i) : [...d, i].sort()))}
                className={`text-xs px-2 py-0.5 rounded-full border ${days.includes(i) ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600'}`} data-testid={`pattern-day-${i}`}>{w}</button>
            ))}
          </div>
          {err && <p className="text-[11px] text-red-600">{err}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={!name.trim() || !days.length} onClick={save} className="px-3 py-1 rounded-md bg-violet-600 text-white text-xs disabled:opacity-40" data-testid="pattern-save">Save pattern</button>
            <button type="button" onClick={() => setSaving(false)} className="px-3 py-1 rounded-md border text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
